// ============================================================================
// ClientHomeworkPanel — trainer-suggested homework (violet / suggested).
// ----------------------------------------------------------------------------
// Lists the client's trainer-prescribed activities (source='suggested'): the
// PENDING ones (status='planned') get a "Mark done" action that POSTs to
// /api/client/me/activities/{id}/complete; completed ones show with a done
// badge. Self-logged (source='own') activities are excluded here — they live on
// the calendar / dashboard. Colour follows the provenance palette (violet) so it
// matches the calendar legend. Mobile-first, Pulse, EN + 繁中.
// ============================================================================
import { useMemo } from "react";
import { motion, useReducedMotion } from "framer-motion";
import { useTranslation } from "react-i18next";
import { Badge, Button, Card, CardHeader, EmptyState } from "../../components";
import { useClientActivities, useCompleteClientActivity } from "../hooks";
import type { Activity } from "../../api";
import { LoadingBlock } from "../../app/PageState";
import { fmtDate } from "./portalLib";
import { ENTRY_STYLE } from "../../dashboard/sessionStyle";
import { cn } from "../../lib/cn";

export function ClientHomeworkPanel() {
  const { t } = useTranslation("activity");
  const query = useClientActivities();

  const { pending, done } = useMemo(() => {
    const all = (query.data ?? []).filter((a) => a.source === "suggested");
    const p = all.filter((a) => a.status !== "completed");
    const d = all.filter((a) => a.status === "completed");
    const at = (a: Activity) =>
      a.scheduled_at ?? a.performed_at ?? a.created_at ?? "";
    p.sort((a, b) => at(a).localeCompare(at(b)));
    d.sort((a, b) => at(b).localeCompare(at(a)));
    return { pending: p, done: d };
  }, [query.data]);

  return (
    <Card>
      <CardHeader title={t("homework.title")} subtitle={t("homework.subtitle")} />
      {query.isLoading ? (
        <LoadingBlock />
      ) : pending.length === 0 && done.length === 0 ? (
        <EmptyState title={t("homework.empty")} className="!py-10" />
      ) : (
        <div className="space-y-5">
          {pending.length > 0 && (
            <Group title={t("homework.due")}>
              {pending.map((a, i) => (
                <HomeworkRow key={a.id} activity={a} index={i} />
              ))}
            </Group>
          )}
          {done.length > 0 && (
            <Group title={t("homework.completed")}>
              {done.slice(0, 8).map((a, i) => (
                <HomeworkRow key={a.id} activity={a} index={i} />
              ))}
            </Group>
          )}
        </div>
      )}
    </Card>
  );
}

function Group({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <div>
      <p className="mb-2 font-display text-xs font-bold uppercase tracking-kicker text-body-soft">
        {title}
      </p>
      <ul className="space-y-2">{children}</ul>
    </div>
  );
}

function HomeworkRow({ activity, index }: { activity: Activity; index: number }) {
  const { t } = useTranslation("activity");
  const reduce = useReducedMotion();
  const complete = useCompleteClientActivity();
  const st = ENTRY_STYLE.activity_suggested;
  const isDone = activity.status === "completed";
  const when = activity.scheduled_at ?? activity.performed_at;

  const metricBits: string[] = [];
  if (activity.sets != null) metricBits.push(t("detail.setsValue", { count: activity.sets }));
  if (activity.reps != null) metricBits.push(t("detail.repsValue", { count: activity.reps }));
  if (activity.duration_min != null)
    metricBits.push(t("detail.minutesValue", { count: activity.duration_min }));

  return (
    <motion.li
      initial={reduce ? false : { opacity: 0, y: 8 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.25, delay: reduce ? 0 : Math.min(index * 0.04, 0.3) }}
    >
      <div
        className={cn(
          "flex items-center gap-3 rounded-md p-3 ring-1",
          isDone ? "bg-surface ring-line" : "bg-violet-soft/50 ring-violet/20",
        )}
      >
        <span className={cn("h-10 w-1.5 shrink-0 rounded-pill", st.grad)} aria-hidden />
        <div className="min-w-0 flex-1">
          <div className="flex flex-wrap items-center gap-2">
            <span className="truncate font-display text-sm font-bold text-body">
              {activity.title || activity.type || t("type.fallback")}
            </span>
            <Badge tone={isDone ? "success" : "ember"} dot className="shrink-0">
              {isDone ? t("homework.done") : t("homework.due")}
            </Badge>
          </div>
          <div className="mt-0.5 flex flex-wrap items-center gap-x-2 text-xs text-body-soft">
            {when && <span className="tabular-nums">{fmtDate(when)}</span>}
            {activity.type && <span className="truncate">{activity.type}</span>}
            {metricBits.length > 0 && <span>{metricBits.join(" · ")}</span>}
          </div>
          {activity.notes && (
            <p className="mt-1 whitespace-pre-line text-xs text-body-muted">
              {activity.notes}
            </p>
          )}
        </div>
        {!isDone && (
          <Button
            size="sm"
            onClick={() => complete.mutate(activity.id)}
            disabled={complete.isPending}
            className="shrink-0"
          >
            {complete.isPending ? t("homework.marking") : t("homework.markDone")}
          </Button>
        )}
      </div>
    </motion.li>
  );
}
